<template>
  <v-container fluid class="fill-height dashboard-container">
    <v-card class="pa-4 mt-4 flex-grow-1" hover>
      <v-card-title class="text-blue-darken-3">{{ $t('message.profile') }}</v-card-title>

      <!-- 移动端搜索区域 -->
      <div class="mobile-search-area d-md-none pa-3">
        <v-text-field
          v-model="taskNameSearch"
          label="Task Name"
          prepend-inner-icon="mdi-magnify"
          clearable
          @input="filterRecords"
          density="compact"
          hide-details
          variant="outlined"
          class="mb-3"
        ></v-text-field>

        <v-text-field
          v-model="dateRangeText"
          label="Task Run Time"
          prepend-inner-icon="mdi-calendar-range"
          readonly
          @click="showDatePicker = true"
          clearable
          @click:clear="clearDateRange"
          density="compact"
          hide-details
          variant="outlined"
          class="mb-3"
        ></v-text-field>

        <v-select
          v-model="selectedModule"
          :items="moduleOptions"
          label="Module"
          prepend-inner-icon="mdi-puzzle"
          clearable
          @update:model-value="filterRecords"
          density="compact"
          hide-details
          variant="outlined"
          class="mb-3"
        ></v-select>

        <v-select
          v-model="mobileSortOption"
          :items="sortOptions"
          label="Sort By"
          prepend-inner-icon="mdi-sort"
          @update:model-value="filterRecords"
          density="compact"
          hide-details
          variant="outlined"
        ></v-select>
      </div>

      <!-- 桌面端数据表 -->
      <v-data-table
          :headers="headers"
          :items="filteredRecords"
          item-key="id"
          class="elevation-1 d-none d-md-block desktop-table"
          dense
          v-model:sort-by="sortBy"
          must-sort
      >
        <!-- 搜索行模板 - 仅桌面端显示 -->
        <template #top>
          <div class="search-row">
            <!-- 任务名称搜索 -->
            <div class="search-cell session-name-column">
              <v-text-field
                v-model="taskNameSearch"
                label="Task Name"
                prepend-inner-icon="mdi-magnify"
                clearable
                @input="filterRecords"
                density="compact"
                hide-details
                variant="outlined"
              ></v-text-field>
            </div>

            <!-- 时间范围搜索 -->
            <div class="search-cell">
              <v-text-field
                v-model="dateRangeText"
                label="Task Run Time"
                prepend-inner-icon="mdi-calendar-range"
                readonly
                @click="showDatePicker = true"
                clearable
                @click:clear="clearDateRange"
                density="compact"
                hide-details
                variant="outlined"
              ></v-text-field>
            </div>

            <!-- 模块搜索 -->
            <div class="search-cell">
              <v-select
                v-model="selectedModule"
                :items="moduleOptions"
                label="Module"
                prepend-inner-icon="mdi-puzzle"
                clearable
                @update:model-value="filterRecords"
                density="compact"
                hide-details
                variant="outlined"
              ></v-select>
            </div>

            <!-- 操作按钮列占位 -->
            <div class="search-cell operations-column">
              <!-- 空占位，保持对齐 -->
            </div>
          </div>
        </template>

        <template #item="{ item }">
          <tr>
            <td class="session-name-column">
              <v-text-field
                  v-model="item.session_name"
                  @blur="updateSessionName(item)"
                  hide-details
                  single-line
              />
            </td>
            <td>{{ item.session_time }}</td>
            <td>{{ item.module_name}}</td>
            <td class="text-left">
              <v-btn @click="navigateToRecordDetail(item)" color="info" variant="tonal" class="mr-2">
                {{ $t('message.detail') }}
              </v-btn>
              <v-btn @click="copyToClipboard(item)" color="error" variant="tonal" class="mr-2">
                {{ $t('message.share') }}
              </v-btn>

              <v-btn @click="downloadAllFiles(item)" color="info" variant="tonal" class="mr-2" :loading="item.downloadLoading">
                {{ $t('message.download') }}
              </v-btn>

              <v-btn @click="deleteSession(item)" color="error" variant="tonal" class="mr-2">
                {{ $t('message.delete') }}
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>

      <!-- 移动端卡片列表 -->
      <div class="mobile-card-list d-md-none">
        <v-card
          v-for="item in filteredRecords"
          :key="item.session_id"
          class="mb-3"
          elevation="2"
        >
          <v-card-text>
            <div class="mobile-card-header mb-3">
              <v-text-field
                v-model="item.session_name"
                @blur="updateSessionName(item)"
                hide-details
                density="compact"
                variant="outlined"
                label="Task Name"
              />
            </div>

            <div class="mobile-info-row mb-2">
              <v-icon small class="mr-2">mdi-clock-outline</v-icon>
              <span class="info-label">Time:</span>
              <span class="info-value">{{ item.session_time }}</span>
            </div>

            <div class="mobile-info-row mb-3">
              <v-icon small class="mr-2">mdi-puzzle-outline</v-icon>
              <span class="info-label">Module:</span>
              <span class="info-value">{{ item.module_name }}</span>
            </div>

            <v-divider class="mb-3"></v-divider>

            <div class="mobile-actions">
              <v-btn
                @click="navigateToRecordDetail(item)"
                color="info"
                variant="tonal"
                size="small"
                block
                class="mb-2"
              >
                <v-icon left>mdi-file-document-outline</v-icon>
                {{ $t('message.detail') }}
              </v-btn>

              <v-btn
                @click="copyToClipboard(item)"
                color="success"
                variant="tonal"
                size="small"
                block
                class="mb-2"
              >
                <v-icon left>mdi-share-variant</v-icon>
                {{ $t('message.share') }}
              </v-btn>

              <v-btn
                @click="downloadAllFiles(item)"
                color="primary"
                variant="tonal"
                size="small"
                block
                class="mb-2"
                :loading="item.downloadLoading"
              >
                <v-icon left>mdi-download</v-icon>
                {{ $t('message.download') }}
              </v-btn>

              <v-btn
                @click="deleteSession(item)"
                color="error"
                variant="tonal"
                size="small"
                block
              >
                <v-icon left>mdi-delete</v-icon>
                {{ $t('message.delete') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- 空状态提示 -->
        <v-card v-if="filteredRecords.length === 0" class="text-center pa-6" elevation="0">
          <v-icon size="64" color="grey-lighten-1">mdi-inbox</v-icon>
          <p class="text-h6 mt-3 text-grey">No records found</p>
        </v-card>
      </div>
      
      <!-- 日期选择对话框 -->
      <v-dialog v-model="showDatePicker" max-width="600px">
        <v-card>
          <v-card-title>Select Date Range</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="endDate"
                  label="End Date"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="showDatePicker = false">Cancel</v-btn>
            <v-btn color="primary" @click="applyDateRange">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>

    <!-- 下载文件名弹窗 -->
    <v-dialog v-model="downloadDialogVisible" max-width="400">
      <v-card>
        <v-card-title>Download Files</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="downloadFileName"
            label="File Name"
            variant="outlined"
            density="compact"
            hide-details
          >
            <template v-slot:append-inner>
              <span class="text-grey">.zip</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="downloadDialogVisible = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmDownload" :loading="currentDownloadSession?.downloadLoading">
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn text @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'UserDashboard',
  data() {
    return {
      headers: [
        // {title:'任务序列号' ,text: '任务序列号', value: 'session_detail'}, // 任务序列号
        {title:'Task Name' ,text: '任务名称', value: 'session_name', width: '30%', sortable: true, sort: (a, b) => b.localeCompare(a)}, // 任务名称 - 反转排序
        {title:'Task Run Time',text: '任务运行时间', value: 'session_time', width: '20%', sortable: true, sort: (a, b) => new Date(b) - new Date(a)}, // 任务运行时间 - 反转排序
        {title:'Module',text: '任务所属模块', value: 'module_name', width: '15%', sortable: true, sort: (a, b) => b.localeCompare(a)}, // 任务所属模块 - 反转排序
        {title:'Operation Button',text: '操作按钮', value: 'operations', align: 'start', width: '35%', sortable: false}, // 操作按钮
        // {title:'CSV 文件',text: 'CSV 文件', value: 'csv_url'}, // CSV 文件
        // {title:'JPG 文件',text: 'JPG 文件', value: 'jpg_url'}, // JPG 文件
      ],
      records: [],
      filteredRecords: [],
      previewImage: "https://cdn.vuetifyjs.com/images/profiles/marcus.jpg",
      rules: [
        value => {
          return !value || !value.length || value[0].size < 2097152 || 'Avatar size should be less than 2 MB!'
        },
      ],
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      // 搜索筛选相关数据
      showDatePicker: false,
      startDate: '',
      endDate: '',
      dateRangeText: '',
      selectedModule: null,
      taskNameSearch: '',
      moduleOptions: [],
      sortBy: [{ key: 'session_time', order: 'asc' }],  // 默认按时间升序排序
      // 移动端排序
      mobileSortOption: 'time_desc',
      sortOptions: [
        { title: 'Time: Newest First', value: 'time_desc' },
        { title: 'Time: Oldest First', value: 'time_asc' },
        { title: 'Name: A to Z', value: 'name_asc' },
        { title: 'Name: Z to A', value: 'name_desc' },
        { title: 'Module: A to Z', value: 'module_asc' },
        { title: 'Module: Z to A', value: 'module_desc' }
      ],
      // 下载弹窗
      downloadDialogVisible: false,
      downloadFileName: '',
      currentDownloadSession: null,
    };
  },
  computed: {
    ...mapGetters(['username']),
  },
  mounted() {
    this.fetchSessionList();
  },
  methods: {
    async fetchSessionList() {
      try {
        let access_token = localStorage.getItem('access_token');
        const response = await axios.post('/api/session/GetSessionListView/', {
          access_token: access_token
        });
        console.log('API Response:', response.data); // 添加调试日志
        this.records = response.data.sessions.map(session => ({
          session_id: session.session_id,//session表的主键
          session: session.session_detail, // 任务序列号，是uuid
          session_time: session.session_time,
          session_name: session.session_name,
          module_name: session.module_name || 'N/A',    // 添加模块名称，如果为空则显示 N/A
          isExample: this.normalizeIsExample(session.isExample ?? session.is_example),
          downloadLoading: false, // 添加下载状态
        }));
        console.log('Processed records:', this.records); // 添加调试日志

        // 生成模块选项
        this.generateModuleOptions();

        // 初始化过滤后的数据并排序
        this.filterRecords();

      } catch (error) {
        console.error('Error fetching session list:', error);
        this.snackbar.message = 'Failed to load sessions.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
      }
    },
    normalizeIsExample(value) {
      if (value === true || value === 'true' || value === 1 || value === '1') {
        return true;
      }
      if (value === false || value === 'false' || value === 0 || value === '0') {
        return false;
      }
      return undefined;
    },
    async updateSessionName(session) {
      try {
        let access_token = localStorage.getItem('access_token');
        const response = await axios.post('/api/session/ChangeSessionName/', {
          access_token: access_token,
          session_id: session.session_id,
          session_name: session.session_name
        });
        console.log(response.data);
        this.snackbar.message = 'Session name updated successfully.';
        this.snackbar.color = 'success';
        this.fetchSessionList();
      } catch (error) {
        console.error('Error updating session name:', error);
        this.snackbar.message = 'Failed to update session name.';
        this.snackbar.color = 'error';
      } finally {
        this.snackbar.show = true;
      }
    },

    async deleteSession(session) {
      try {
        let access_token = localStorage.getItem('access_token');
        const response = await axios.post('/api/session/DeleteSession/', {
          access_token: access_token,
          session_id: session.session_id
        });
        console.log(response.data);
        this.snackbar.message = 'Session  deleted successfully.';
        this.snackbar.color = 'success';
        this.fetchSessionList();
      } catch (error) {
        console.error('Error updating session name:', error);
        this.snackbar.message = 'Failed to delete session name.';
        this.snackbar.color = 'error';
      } finally {
        this.snackbar.show = true;
      }
    },
    // 打开下载弹窗
    downloadAllFiles(session) {
      if (!session.session) {
        this.snackbar.message = 'Session ID is missing.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
        return;
      }

      const moduleName = session.module_name || 'ebio';
      // 示例数据使用 'example' 作为文件名后缀
      const fileNameSuffix = session.isExample === true ? 'example' : session.session;
      this.downloadFileName = `${moduleName}_summary_${fileNameSuffix}`;
      this.currentDownloadSession = session;
      this.downloadDialogVisible = true;
    },

    // 确认下载
    async confirmDownload() {
      const session = this.currentDownloadSession;
      if (!session || !this.downloadFileName.trim()) {
        this.snackbar.message = 'Please enter a file name.';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
        return;
      }

      try {
        session.downloadLoading = true;
        console.log('开始下载所有文件，session:', session.session, 'isExample:', session.isExample);

        const moduleName = session.module_name || 'ebio';
        // 示例数据使用 'example' 作为路径，普通数据使用实际的 session UUID
        const downloadPath = session.isExample === true ? 'example' : session.session;
        const downloadUrl = `/api/session/Summary/download/${downloadPath}/${moduleName}/`;

        const response = await axios.post(downloadUrl, {}, {
          responseType: 'blob'
        });

        console.log('下载响应状态:', response.status);

        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.downloadFileName.trim()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('文件下载成功');
        this.downloadDialogVisible = false;
        this.snackbar.message = 'Files downloaded successfully.';
        this.snackbar.color = 'success';

      } catch (error) {
        console.error('下载文件失败:', error);

        if (error.response) {
          if (error.response.status === 404) {
            this.snackbar.message = 'Download link not found, please check session status.';
          } else if (error.response.status === 500) {
            this.snackbar.message = 'Server internal error, please try again later.';
          } else {
            this.snackbar.message = `Download failed, error code: ${error.response.status}`;
          }
        } else {
          this.snackbar.message = 'Download failed: ' + error.message;
        }
        this.snackbar.color = 'error';

      } finally {
        session.downloadLoading = false;
        this.snackbar.show = true;
      }
    },
    navigateToRecordDetail(record) {
      const params = new URLSearchParams({ session: record.session });
      if (record.isExample === true) {
        params.set('isExample', 'true');
        if (record.module_name) {
          params.set('module', record.module_name);
        }
      }
      const url = `/record-detail?${params.toString()}`;
      window.open(url, '_blank');
    },
    copyToClipboard(record) {
      const baseUrl = window.location.origin;
      const params = new URLSearchParams({ session: record.session });
      if (record.isExample === true) {
        params.set('isExample', 'true');
        if (record.module_name) {
          params.set('module', record.module_name);
        }
      }
      const url = `${baseUrl}/record-detail?${params.toString()}`;
      
      // 生成分享消息格式
      const shareMessage = this.generateShareMessage(record, url);
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareMessage).then(() => {
          this.snackbar.message = 'Share message copied to clipboard';
          this.snackbar.color = 'success';
        }).catch(err => {
          this.snackbar.message = `Failed to copy text to clipboard: ${err}`;
          this.snackbar.color = 'error';
        }).finally(() => {
          this.snackbar.show = true;
        });
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareMessage;
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          this.snackbar.message = 'Share message copied to clipboard';
          this.snackbar.color = 'success';
        } catch (err) {
          this.snackbar.message = `Failed to copy text to clipboard: ${err}`;
          this.snackbar.color = 'error';
        } finally {
          document.body.removeChild(textarea);
          this.snackbar.show = true;
        }
      }
    },
    
    // 生成分享消息
    generateShareMessage(record, url) {
      const shareText = `${this.username || 'Unknown'} shared ExposomeX website data with you:
Task Name: ${record.session_name}
Time: ${record.session_time}
Module: ${record.module_name}
Results: ${url}`;
      
      return shareText;
    },
    
    // 生成模块选项
    generateModuleOptions() {
      const modules = [...new Set(this.records.map(record => record.module_name))];
      this.moduleOptions = modules.map(module => ({
        title: module,
        value: module
      }));
    },
    
    // 应用日期范围
    applyDateRange() {
      if (this.startDate && this.endDate) {
        this.dateRangeText = `${this.startDate} to ${this.endDate}`;
      } else if (this.startDate) {
        this.dateRangeText = `From ${this.startDate}`;
      } else if (this.endDate) {
        this.dateRangeText = `Until ${this.endDate}`;
      }
      this.showDatePicker = false;
      this.filterRecords();
    },
    
    // 清除日期范围
    clearDateRange() {
      this.startDate = '';
      this.endDate = '';
      this.dateRangeText = '';
      this.filterRecords();
    },
    
    // 过滤记录
    filterRecords() {
      let filtered = [...this.records];

      // 按任务名称过滤
      if (this.taskNameSearch) {
        const searchTerm = this.taskNameSearch.toLowerCase();
        filtered = filtered.filter(record =>
          record.session_name.toLowerCase().includes(searchTerm)
        );
      }

      // 按模块过滤
      if (this.selectedModule) {
        filtered = filtered.filter(record =>
          record.module_name === this.selectedModule
        );
      }

      // 按日期范围过滤
      if (this.startDate || this.endDate) {
        filtered = filtered.filter(record => {
          const recordDate = new Date(record.session_time);
          const start = this.startDate ? new Date(this.startDate) : null;
          const end = this.endDate ? new Date(this.endDate) : null;

          if (start && end) {
            return recordDate >= start && recordDate <= end;
          } else if (start) {
            return recordDate >= start;
          } else if (end) {
            return recordDate <= end;
          }
          return true;
        });
      }

      // 根据选择的排序选项进行排序
      switch (this.mobileSortOption) {
        case 'time_desc':
          // 时间倒序（最新的在前面）
          filtered.sort((a, b) => new Date(b.session_time) - new Date(a.session_time));
          break;
        case 'time_asc':
          // 时间升序（最旧的在前面）
          filtered.sort((a, b) => new Date(a.session_time) - new Date(b.session_time));
          break;
        case 'name_asc':
          // 名称升序（A to Z）
          filtered.sort((a, b) => a.session_name.localeCompare(b.session_name));
          break;
        case 'name_desc':
          // 名称降序（Z to A）
          filtered.sort((a, b) => b.session_name.localeCompare(a.session_name));
          break;
        case 'module_asc':
          // 模块升序（A to Z）
          filtered.sort((a, b) => a.module_name.localeCompare(b.module_name));
          break;
        case 'module_desc':
          // 模块降序（Z to A）
          filtered.sort((a, b) => b.module_name.localeCompare(a.module_name));
          break;
        default:
          // 默认按时间倒序
          filtered.sort((a, b) => new Date(b.session_time) - new Date(a.session_time));
      }

      this.filteredRecords = filtered;
    },
    
    onFileChange(e) {
      console.log(e.target.files[0])
      let file = e.target.files[0]
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewImage = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
  }
};
</script>

<style scoped>
.fill-height {
  height: 100vh;
}

.text-right {
  text-align: right;
}

/* 响应式卡片宽度 */
.v-card {
  width: 80%;
  margin: 0 auto;
}

/* 平板端调整 */
@media (max-width: 960px) {
  .v-card {
    width: 90%;
  }
}

/* 移动端调整 */
@media (max-width: 600px) {
  .v-card {
    width: 100%;
    margin: 0;
    border-radius: 0;
  }

  .dashboard-container {
    padding: 0 !important;
  }

  .v-card-title {
    font-size: 18px !important;
    padding: 12px 16px !important;
  }
}

.v-data-table td {
  padding: 8px;
}

/* ========== 桌面端样式 ========== */
/* 搜索行样式 - 与表格列完美对齐 */
.search-row {
  display: table;
  width: 100%;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
}

.search-cell {
  display: table-cell;
  padding: 12px 16px;
  vertical-align: middle;
}

/* 调整各列的宽度以匹配表格 */
.session-name-column {
  width: 30%;
}

.search-cell:nth-child(2) {
  width: 20%;
}

.search-cell:nth-child(3) {
  width: 15%;
}

.operations-column {
  width: 35%;
  text-align: left;
}

.v-data-table .v-btn {
  margin: 0;
}

/* 确保表格单元格与搜索框对齐 */
.v-data-table td {
  padding: 8px 16px;
  vertical-align: middle;
  font-size: 16px;
}

/* 调整表格头部样式以匹配搜索行 */
:deep(.v-data-table th) {
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
}

/* 搜索框样式优化 */
.search-row .v-text-field,
.search-row .v-select {
  margin: 0;
}

.search-row .v-field {
  background-color: white;
}

/* 自定义标题字体大小 */
.v-card-title {
  font-size: 16px !important;
}

/* 排序图标样式优化 */
:deep(.v-data-table th.sortable) {
  cursor: pointer;
  user-select: none;
}

:deep(.v-data-table th.sortable:hover) {
  background-color: #f5f5f5;
}

/* 确保所有排序图标可见并使用蓝色 */
:deep(.v-data-table-header__sort-icon) {
  opacity: 1 !important;
  font-size: 18px;
  color: #1566C0 !important;
}

/* 所有可排序列的图标都显示 */
:deep(.v-data-table th.sortable .v-data-table-header__sort-icon) {
  opacity: 1 !important;
  color: #1566C0 !important;
}

/* 所有图标使用蓝色 */
:deep(.v-data-table th .v-icon) {
  color: #1566C0 !important;
}

/* 降序排序时，箭头朝下 */
:deep(.v-data-table th[aria-sort="descending"] .v-data-table-header__sort-icon) {
  transform: rotate(0deg);
}

/* 升序排序时，箭头朝上 */
:deep(.v-data-table th[aria-sort="ascending"] .v-data-table-header__sort-icon) {
  transform: rotate(180deg);
}

/* 未激活的列也显示降序箭头（朝下） */
:deep(.v-data-table th.sortable:not([aria-sort]) .v-data-table-header__sort-icon) {
  transform: rotate(0deg);
  opacity: 0.5 !important;
}

/* ========== 移动端样式 ========== */
/* 移动端搜索区域 */
.mobile-search-area {
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
}

/* 移动端卡片列表 */
.mobile-card-list {
  padding: 8px;
}

.mobile-card-list .v-card {
  border-radius: 12px;
  overflow: hidden;
}

/* 移动端卡片头部 */
.mobile-card-header {
  padding: 0;
}

/* 移动端信息行 */
.mobile-info-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #616161;
}

.mobile-info-row .info-label {
  font-weight: 500;
  margin-right: 8px;
  margin-left: 4px;
  min-width: 60px;
  color: #757575;
}

.mobile-info-row .info-value {
  flex: 1;
  color: #424242;
}

/* 移动端操作按钮 */
.mobile-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-actions .v-btn {
  justify-content: flex-start;
  text-transform: none;
  letter-spacing: normal;
}

.mobile-actions .v-btn :deep(.v-icon) {
  margin-right: 8px;
}

/* 移动端空状态 */
.mobile-card-list .text-center {
  opacity: 0.6;
}

/* 移动端优化按钮间距 */
@media (max-width: 600px) {
  .mobile-actions .v-btn {
    height: 44px;
    font-size: 14px;
  }
}
</style>
